import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAuthToken } from "../../hook";
import { testConnection } from "../../hook/testConnection";
import { cancelClaudeLogin, finishClaudeLogin, isClaudeSignInUrl, startClaudeLogin } from "../../api/llmLoginFlow";
import { LoginKind } from "../../hook/modelProvider";
import { useAgentProvider } from "../../hook/agentProvider";
import { CasLoader } from "../brand/CasLoader";

const parseTargets = (value: string) =>
  value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

const CLAUDE_SETUP_COMMAND = "claude setup-token";
const CLAUDE_TOKEN_PREFIX = "sk-ant-oat01-";
const isClaudeSetupToken = (value: string) =>
  value.startsWith(CLAUDE_TOKEN_PREFIX) && value.length >= 40;

// Settings › Agent provider. Plain words first: the active provider, whether the Claude
// subscription is connected and when it expires, and the model the next chat will use. The swap
// is two buttons (not a form); Advanced hides the service-account and paste-a-token paths.
export const ModelProviderSettings: React.FC = () => {
  const {
    provider,
    accountProvider,
    status,
    loaded,
    models,
    selectedModel,
    chooseProvider,
    chooseModel,
    chooseDefaultModel,
    usesDefaultModel,
    defaultModelLabel,
    disconnectClaude,
    storeLogin,
    accountKey,
    setAccountKey,
    reload,
    readFailed,
    error: readError,
  } = useAgentProvider();

  const [account, setAccount] = useState(accountKey);
  const [secret, setSecret] = useState("");
  const [kind, setKind] = useState<LoginKind>("oauth-token");
  const [targets, setTargets] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupSecret, setSetupSecret] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupError, setSetupError] = useState("");
  const [copyLabel, setCopyLabel] = useState("Copy");
  const [testResult, setTestResult] = useState("");
  const [testing, setTesting] = useState(false);
  const [flow, setFlow] = useState<"idle" | "starting" | "waiting" | "finishing">("idle");
  const [loginId, setLoginId] = useState("");
  const [signInUrl, setSignInUrl] = useState("");
  const [flowCode, setFlowCode] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // A dialog generation: closing (or reopening) bumps it, so an in-flight start/finish that
  // resolves later cannot reopen, advance or write into the closed/reopened dialog.
  const flowGenRef = useRef(0);

  useEffect(() => { setAccount(accountKey); }, [accountKey]);

  // The sign-in routes authenticate with the app's token; ask the same source the chat uses.
  const [authToken] = useAuthToken();

  const changeProvider = async (choice: "openrouter" | "anthropic") => {
    setError("");
    setNotice("");
    setBusy(true);
    try {
      await chooseProvider(choice);
      setNotice(choice === "anthropic" ? "Now using your Claude subscription." : "Now using OpenRouter.");
    } catch (e: any) {
      setError(e?.message ?? "Could not change the provider.");
    } finally {
      setBusy(false);
    }
  };

  const saveLogin = async () => {
    if (!secret) { setError("Paste a login token or API key first."); return; }
    setBusy(true);
    setError("");
    try {
      await storeLogin(kind, secret);
      setSecret("");
      setNotice("Login saved.");
    } catch (e: any) {
      setSecret("");
      setError(e?.message ?? "login write failed");
    } finally {
      setBusy(false);
    }
  };

  const removeLogin = async () => {
    setError("");
    setBusy(true);
    try {
      await disconnectClaude();
      setNotice("Claude disconnected. Chats on Claude will stop until you connect again.");
    } catch (e: any) {
      setError(e?.message ?? "Could not disconnect Claude.");
    } finally {
      setBusy(false);
    }
  };

  const openSetup = () => {
    flowGenRef.current += 1;
    setSetupSecret("");
    setSetupError("");
    setTestResult("");
    setCopyLabel("Copy");
    setSetupOpen(true);
  };

  const closeSetup = () => {
    flowGenRef.current += 1;
    // A start still in flight has no login id to cancel by, but the bridge's cancel ends the
    // account's pending sign-in; a finish in flight must also be cancelled, not left running.
    if ((flow === "starting" || flow === "waiting" || flow === "finishing") && authToken) {
      void cancelClaudeLogin(authToken).catch(() => {});
    }
    setFlow("idle");
    setLoginId("");
    setSignInUrl("");
    setFlowCode("");
    setSetupSecret("");
    setSetupError("");
    setTestResult("");
    setSetupOpen(false);
  };

  const copySetupCommand = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(CLAUDE_SETUP_COMMAND);
      setCopyLabel("Copied");
    } catch {
      setCopyLabel("Copy failed");
    }
  };

  const verifyAndStore = async () => {
    setSetupError("");
    if (!isClaudeSetupToken(setupSecret)) {
      setSetupError("That token does not look right: it should start with sk-ant-oat01- and be at least 40 characters.");
      return;
    }
    setSetupBusy(true);
    try {
      await storeLogin("oauth-token", setupSecret);
      setSetupSecret("");
      setNotice("Claude connected.");
    } catch (e: any) {
      setSetupSecret("");
      setSetupError(e?.message ?? "login write failed");
    } finally {
      setSetupBusy(false);
    }
  };

  const beginSignIn = async () => {
    if (!authToken) return;
    setSetupError("");
    const generation = ++flowGenRef.current;
    setFlow("starting");
    try {
      const started = await startClaudeLogin(authToken as string);
      if (generation !== flowGenRef.current) return;
      if (!isClaudeSignInUrl(started.url)) {
        // Do not render a link to an unexpected host; end the login the bridge just created.
        if (authToken) void cancelClaudeLogin(authToken).catch(() => {});
        setFlow("idle");
        setSetupError("The sign-in service returned an unexpected address, so no link was opened. Start the sign-in again.");
        return;
      }
      setLoginId(started.loginId);
      setSignInUrl(started.url);
      setFlow("waiting");
    } catch (e: any) {
      if (generation !== flowGenRef.current) return;
      setFlow("idle");
      setSetupError(e?.message ?? "Could not start the sign-in.");
    }
  };

  const finishSignIn = async () => {
    if (!authToken) return;
    setSetupError("");
    const generation = ++flowGenRef.current;
    setFlow("finishing");
    try {
      await finishClaudeLogin(authToken as string, loginId, flowCode.trim());
      if (generation !== flowGenRef.current) return;
      setFlowCode("");
      setFlow("idle");
      await reload();
      if (generation !== flowGenRef.current) return;
      setNotice("Claude connected.");
      setSetupOpen(false);
    } catch (e: any) {
      if (generation !== flowGenRef.current) return;
      setFlowCode("");
      setSetupError(e?.message ?? "The sign-in could not be finished.");
      // Server codes (omega-ai-agent-service/src/llmLogin/routes.ts): LOGIN_CODE_INVALID (400)
      // means the code is not shaped like one, so the dialog stays open for a retype.
      // LOGIN_CODE_REJECTED (422), LOGIN_SHORT_LIVED/LOGIN_BAD_RESPONSE (502) and
      // LOGIN_CANCELLED (409) are terminal: Claude will not accept that code, so clearing it
      // and returning to Start sign-in is the only way forward.
      setFlow(e?.code === "LOGIN_CODE_INVALID" ? "waiting" : "idle");
    }
  };

  const runTestConnection = async () => {
    setTesting(true);
    setTestResult("");
    try {
      setTestResult(await testConnection(account || undefined));
    } catch (e: any) {
      setTestResult(e?.message ?? "connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const assign = async () => {
    const list = parseTargets(targets);
    setError("");
    if (!account) { setError("Enter the account to lend your login to first."); return; }
    if (list.length === 0) { setError("List at least one service account."); return; }
    try {
      const { assignLogin } = await import("../../hook/modelProvider");
      await assignLogin(account, list);
      setNotice(`Login lent to ${list.length} account(s).`);
    } catch (e: any) {
      setError(e?.message ?? "assign failed");
    }
  };

  const unlink = async () => {
    const list = parseTargets(targets);
    setError("");
    if (list.length === 0) { setError("List at least one service account."); return; }
    try {
      const { unlinkLogin } = await import("../../hook/modelProvider");
      await unlinkLogin(list);
      setNotice(`Unlinked ${list.length} account(s).`);
    } catch (e: any) {
      setError(e?.message ?? "unlink failed");
    }
  };

  const changeAccount = (value: string) => {
    setAccount(value);
  };

  const connectable = !account;
  // A Claude swap is only offered when there is a login that can serve a chat; otherwise the
  // control says what to do first instead of leaving the user with a provider that cannot run.
  const claudeReady = status.claudeState === "valid" || status.claudeState === "expiring";
  // Before the account row is read, neither rule applies: no disabled flash and no false warning.
  const claudeBlocked = loaded && !claudeReady;

  return (
    <Stack gap={2} data-testid="model-provider-settings">
      <Typography variant="h6">Agent provider</Typography>

      <Stack gap={0.5}>
        <Typography variant="body1" data-testid="active-provider">
          Active provider: <strong>{readFailed ? "unavailable" : status.active}</strong>
        </Typography>
        <Typography variant="body2" data-testid="claude-status" data-state={loaded ? status.claudeState : "loading"}>
          Claude subscription: {loaded ? status.claude : "Checking\u2026"}
        </Typography>
        <Typography variant="body2" data-testid="next-model">
          Model for your next chat: <strong>{models.find((m) => m.id === selectedModel)?.name ?? selectedModel}</strong>
          {usesDefaultModel ? " (default)" : ""}
        </Typography>
        {!accountKey && provider !== accountProvider && (
          <Typography variant="body2" data-testid="chat-override">
            This tab's chats were switched to {provider === "anthropic" ? "your Claude subscription" : "OpenRouter"} from
            the chat; the Account setting is {accountProvider === "anthropic" ? "your Claude subscription" : "OpenRouter"}.
            Choosing a provider here clears that switch.
          </Typography>
        )}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
        <Stack gap={0.5} sx={{ flex: 1 }}>
          <Button
            variant={provider === "openrouter" ? "contained" : "outlined"}
            onClick={() => void changeProvider("openrouter")}
            disabled={busy}
            data-testid="use-openrouter"
          >
            Use OpenRouter
          </Button>
          <Typography variant="body2">
            Runs chats on OpenRouter's models. Uses your OmegaAI credits.
          </Typography>
        </Stack>
        <Stack gap={0.5} sx={{ flex: 1 }}>
          <Button
            variant={provider === "anthropic" ? "contained" : "outlined"}
            onClick={() => void changeProvider("anthropic")}
            disabled={busy || !loaded || claudeBlocked}
            startIcon={loaded ? undefined : <CasLoader size={14} data-testid="use-claude-loading" />}
            data-testid="use-claude"
          >
            Use my Claude subscription
          </Button>
          <Typography variant="body2" data-testid="use-claude-explainer">
            {claudeReady
              ? "Runs chats on your Claude plan. Uses your Claude usage, not OmegaAI credits."
              : "Connect Claude first; then chats can run on your Claude plan."}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <Button variant="outlined" onClick={openSetup} data-testid="connect-claude">
          Connect Claude
        </Button>
        <Button
          variant="text"
          color="warning"
          onClick={() => void removeLogin()}
          disabled={busy || status.claudeState === "none"}
          data-testid="disconnect-claude"
        >
          Disconnect Claude
        </Button>
        <Typography variant="caption" data-testid="disconnect-explainer">
          Disconnecting removes the saved Claude login from this account; chats on Claude stop until you connect again.
        </Typography>
      </Stack>

      <TextField
        select
        size="small"
        label="Model for your next chat"
        value={selectedModel}
        onChange={(e) => void chooseModel(e.target.value)}
        SelectProps={{ inputProps: { "data-testid": "settings-model" } }}
        sx={{ maxWidth: 360 }}
      >
        {models.map((model) => (
          <MenuItem key={model.id} value={model.id}>
            {model.name}
          </MenuItem>
        ))}
      </TextField>

      <Button
        variant="text"
        onClick={() => void chooseDefaultModel()}
        disabled={!loaded || usesDefaultModel}
        data-testid="model-default"
      >
        Use the default model
      </Button>
      <Typography variant="caption" data-testid="model-default-explainer">
        Removes the saved model for {accountKey ? "this account" : "your account"}. New chats then run on {defaultModelLabel}
        {accountKey ? ", or on the model of the account it belongs to" : ""}.
      </Typography>

      {provider === "anthropic" && claudeBlocked && (
        <Alert severity="warning" data-testid="claude-not-connected">
          Your Claude subscription is selected but not connected, so chats cannot run on it.
          Connect Claude, or choose OpenRouter.
        </Alert>
      )}
      {status.claudeState === "expiring" && (
        <Alert severity="warning" data-testid="login-expiring">
          Your Claude subscription expires soon ({status.expires}). Reconnect Claude to keep using it.
        </Alert>
      )}
      {status.claudeState === "expired" && (
        <Alert severity="error" data-testid="login-expired">
          Your Claude subscription has expired. Reconnect Claude, or use OpenRouter.
        </Alert>
      )}

      {readFailed && (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => void reload()} data-testid="retry-account-read">
              Retry
            </Button>
          }
        >
          {readError || "Could not read the account's provider setting."}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}

      <Accordion expanded={advancedOpen} onChange={() => setAdvancedOpen((open) => !open)} data-testid="advanced">
        <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="advanced-toggle">
          <Typography variant="subtitle2">Advanced</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {advancedOpen && (
            <Stack gap={2} data-testid="advanced-panel">
              <TextField
                size="small"
                label="Account"
                value={account}
                onChange={(e) => changeAccount(e.target.value)}
                onBlur={() => setAccountKey(account.trim())}
                helperText="Blank for your own account, or a service-account:ID"
                inputProps={{ "data-testid": "account-field" }}
              />

              <Stack gap={1}>
                <Typography variant="subtitle2">Sign in with a token you made yourself</Typography>
                <Typography variant="body2">
                  Run this command in your terminal, then paste the token it prints.
                </Typography>
                <Stack direction="row" gap={1}>
                  <TextField
                    size="small"
                    value={CLAUDE_SETUP_COMMAND}
                    InputProps={{ readOnly: true }}
                    inputProps={{ "data-testid": "setup-command", "aria-label": "Setup command" }}
                    sx={{ flex: 1 }}
                  />
                  <Button variant="outlined" onClick={() => void copySetupCommand()}>
                    {copyLabel}
                  </Button>
                </Stack>
                <TextField
                  size="small"
                  type="password"
                  label="Paste the token"
                  autoComplete="new-password"
                  value={setupSecret}
                  onChange={(e) => setSetupSecret(e.target.value)}
                  inputProps={{ "data-testid": "setup-token" }}
                />
                <Stack direction="row" gap={1} alignItems="center">
                  <Button variant="outlined" disabled={setupBusy} onClick={() => void verifyAndStore()}>
                    Verify and store
                  </Button>
                  <Button variant="outlined" disabled={testing} onClick={() => void runTestConnection()}>
                    Test connection
                  </Button>
                  {testResult && (
                    <Typography variant="body2" data-testid="test-connection-result">
                      {testResult}
                    </Typography>
                  )}
                </Stack>
              </Stack>

              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <TextField
                  select
                  size="small"
                  label="Login kind"
                  value={kind}
                  onChange={(e) => setKind(e.target.value as LoginKind)}
                  sx={{ width: 160 }}
                >
                  <MenuItem value="oauth-token">oauth-token</MenuItem>
                  <MenuItem value="api-key">api-key</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  type="password"
                  label="Login token or API key"
                  autoComplete="new-password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  inputProps={{ "data-testid": "login-secret" }}
                  sx={{ flex: 1 }}
                />
                <Button variant="outlined" disabled={busy} onClick={() => void saveLogin()}>
                  Store login
                </Button>
              </Stack>

              <Typography variant="subtitle2">Lend my login to service accounts</Typography>
              <TextField
                multiline
                minRows={3}
                size="small"
                label="Account keys, one per line"
                value={targets}
                onChange={(e) => setTargets(e.target.value)}
                inputProps={{ "data-testid": "assign-targets" }}
                helperText="One service-account:ID per line"
              />
              <Stack direction="row" gap={1}>
                <Button variant="contained" onClick={() => void assign()}>Lend my login</Button>
                <Button variant="outlined" onClick={() => void unlink()}>Unlink</Button>
              </Stack>

              <Typography variant="subtitle2">Redacted account summary</Typography>
              <pre data-testid="llm-account-summary" style={{ fontSize: 12, margin: 0 }}>
                {JSON.stringify(
                  { "account-id": account || "", "model-provider": provider, login: status.claudeState },
                  null,
                  2,
                )}
              </pre>
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog open={setupOpen} onClose={closeSetup} fullWidth maxWidth="sm">
        <DialogTitle>Connect Claude</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            {connectable ? (
              <Stack gap={1} data-testid="connect-flow">
                <Typography variant="body2">
                  Sign in to Claude in your own browser. OmegaAI sends you to Claude's own sign-in page and trades only
                  the code Claude shows you for a token; it never sees your Claude password.
                </Typography>
                {flow === "idle" && (
                  <Button variant="contained" disabled={!authToken} onClick={() => void beginSignIn()} data-testid="connect-start">
                    Start sign-in
                  </Button>
                )}
                {flow === "starting" && <Typography variant="body2">Starting the sign-in</Typography>}
                {(flow === "waiting" || flow === "finishing") && (
                  <Stack gap={1}>
                    <Typography variant="body2">
                      1.{" "}
                      <a href={signInUrl} target="_blank" rel="noopener noreferrer" data-testid="signin-link">
                        Open the Claude sign-in
                      </a>{" "}
                      and approve access.
                    </Typography>
                    <Typography variant="body2">2. Claude shows a code. Paste it here.</Typography>
                    <TextField
                      size="small"
                      type="password"
                      label="Code from Claude"
                      autoComplete="off"
                      value={flowCode}
                      onChange={(e) => setFlowCode(e.target.value)}
                      inputProps={{ "data-testid": "connect-code" }}
                    />
                    <Button
                      variant="contained"
                      disabled={flow === "finishing" || !flowCode.trim()}
                      onClick={() => void finishSignIn()}
                      data-testid="connect-finish"
                    >
                      Finish sign-in
                    </Button>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" data-testid="connect-flow-hint">
                To connect Claude for another account, enter it under Advanced, then connect it there.
              </Typography>
            )}
            {setupError && <Alert severity="error">{setupError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSetup}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
